import { useState, type FormEvent } from "react";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import PageHeader from "@/components/base/PageHeader";

export default function Contact() {
  useDocumentMeta({
    title: "联系与反馈 - 舒淇明星官网",
    description:
      "舒淇明星官网联系与反馈页面，欢迎影迷通过邮件或反馈表单与我们联系，分享你对舒淇作品的喜爱与建议，我们将认真对待每一条留言。",
    keywords: "舒淇官网联系,舒淇反馈,联系我们",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle"
  );
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/db74e43askivmpnrcfp0",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      const isSuccess = response.ok && parsed?.code === "OK";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (isSuccess && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && String(serverMsg).trim().length > 0
            ? serverMsg
            : "提交失败，请稍后重试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请检查网络后重试。");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Contact"
        title="联系与反馈"
        description="舒淇官网是面向广大影迷的非商业信息门户，欢迎通过以下方式与我们交流作品心得与建议。"
      />

      <section className="max-w-content mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                关于本站
              </h2>
              <span className="block h-px w-12 bg-accent-400 mt-4 mb-5" />
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                本站致力于为影迷提供舒淇的详细个人资料、影视作品介绍与最新动态资讯，
                所有内容均用于影迷交流与参考，帮助你更全面地了解舒淇的银幕世界。
              </p>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 space-y-5">
              <h3 className="font-heading text-base font-semibold text-foreground-950">
                联系方式
              </h3>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-mail-line text-lg" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                  <p className="text-sm text-foreground-600">contact@shuqi-fan.example</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-time-line text-lg" />
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-950">回复时间</p>
                  <p className="text-sm text-foreground-600">
                    我们通常在 2-3 个工作日内回复
                  </p>
                </div>
              </div>
              <p className="text-xs text-secondary-500 leading-relaxed">
                注：以上邮箱为示例信息，本站为非商业影迷站点。
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                意见反馈
              </h2>
              <span className="block h-px w-12 bg-accent-400 mt-4 mb-6" />

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的姓名"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-secondary-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="请输入你的邮箱地址"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-secondary-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100"
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-sm font-medium text-foreground-800 mb-2"
                  >
                    反馈内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请写下你的想法或建议（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 placeholder:text-secondary-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 resize-none"
                  />
                </div>

                <input
                  className="form-aux-field"
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-lg" />
                      提交中…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-lg" />
                      提交反馈
                    </>
                  )}
                </button>

                {status === "success" && (
                  <div className="flex items-center gap-2 rounded-md bg-accent-50 border border-accent-200 px-4 py-3 text-sm text-accent-700">
                    <i className="ri-checkbox-circle-line text-lg" />
                    感谢你的反馈！我们已收到你的留言，会尽快回复你。
                  </div>
                )}

                {status === "error" && (
                  <div className="flex items-center gap-2 rounded-md bg-background-100 border border-background-300 px-4 py-3 text-sm text-foreground-700">
                    <i className="ri-error-warning-line text-lg text-accent-600" />
                    {formError}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}